import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import WindowTypeCard from "./components/WindowTypeCard";
import SpecsComparison from "./components/SpecsComparison";
import { windowStyles, energyFeatures, windowTestimonials, windowFaqs } from "../../mocks/windows";

const surreyCoverage = [
  "Caterham", "Redhill", "Reigate", "Oxted", "Warlingham", "Purley",
  "Coulsdon", "Whyteleafe", "Tandridge", "Godstone", "Lingfield", "Banstead",
];

const processSteps = [
  { step: "1", title: "Free Home Survey", desc: "We visit your home, assess your existing windows, measure every opening and advise on style, material and colour options." },
  { step: "2", title: "Written Quotation", desc: "You receive a clear itemised quote within 48 hours — broken down per window, fully fitted. No hidden charges." },
  { step: "3", title: "Manufacture", desc: "Your windows are made to your exact measurements in the UK, typically within 2–4 weeks of order confirmation." },
  { step: "4", title: "Expert Installation", desc: "Our FENSA-registered installers fit your windows to the highest standard, leaving your home clean and secure." },
];

export default function WindowsPage() {
  const [activeFilter, setActiveFilter] = useState<"all" | "popular" | "premium" | "specialist">("all");
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", windowType: "", message: "" });
  const formRef = useRef<HTMLElement>(null);

  useEffect(() => {
    document.title = "uPVC & Aluminium Windows Caterham Surrey | Ensure Windows";
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute("content", "A-rated uPVC and aluminium windows installed across Caterham, Surrey and South London. Free surveys, written quotes, FENSA registered. Call 07852 636 300.");
    return () => {
      document.title = "Ensure Windows Caterham | Premium Windows & Doors Surrey";
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await fetch("https://readdy.ai/api/form/d722voc991ove783t9rg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(form as Record<string, string>).toString(),
      });
    } finally {
      setSubmitted(true);
      setSubmitting(false);
    }
  };

  const handleQuote = (windowName: string) => {
    setForm((f) => ({ ...f, windowType: windowName }));
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const filtered = activeFilter === "all" ? windowStyles : windowStyles.filter((w) => w.category === activeFilter);

  return (
    <main>
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[88vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/c4b113682a5617a346c63b427e74971a.jpg"
            alt="uPVC and aluminium windows Surrey"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/55 to-black/20"></div>
        </div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pt-24">
          <div className="max-w-2xl">
            <nav className="flex items-center gap-2 text-xs text-stone-400 mb-6" aria-label="Breadcrumb" style={{ fontFamily: "'Raleway', sans-serif" }}>
              <Link to="/" className="hover:text-amber-400 transition-colors cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to="/products" className="hover:text-amber-400 transition-colors cursor-pointer">Products</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-white">Windows</span>
            </nav>
            <span className="inline-block text-amber-400 text-xs font-bold tracking-widest uppercase border border-amber-400/50 px-4 py-1.5 rounded-full mb-5" style={{ fontFamily: "'Raleway', sans-serif" }}>
              Caterham · Surrey · South London
            </span>
            <h1 className="text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-tight mb-5" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              uPVC &amp; Aluminium Windows in Surrey
            </h1>
            <p className="text-lg text-stone-200 leading-relaxed mb-8" style={{ fontFamily: "'Raleway', sans-serif" }}>
              A-rated energy-efficient windows, expertly installed across Caterham and Surrey. Six styles, two materials, outstanding thermal performance — supply and fit from your local FENSA-registered installer.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="#quote-form" className="bg-amber-600 hover:bg-amber-500 text-white font-semibold px-8 py-4 rounded-md transition-colors whitespace-nowrap cursor-pointer" style={{ fontFamily: "'Raleway', sans-serif" }}>
                Get a Free Quote
              </a>
              <a href="#window-styles" className="border border-white/60 hover:border-white text-white hover:bg-white/10 font-semibold px-8 py-4 rounded-md transition-all whitespace-nowrap cursor-pointer" style={{ fontFamily: "'Raleway', sans-serif" }}>
                Explore Window Styles
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="bg-zinc-900 py-8">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-5 gap-4 divide-x divide-white/10">
          {[
            { icon: "ri-award-line", v: "A+", l: "Energy Rating" },
            { icon: "ri-shield-check-line", v: "FENSA", l: "Registered" },
            { icon: "ri-temp-cold-line", v: "1.2", l: "U-Value W/m²K" },
            { icon: "ri-tools-line", v: "10 yr", l: "Guarantee" },
            { icon: "ri-calendar-check-line", v: "2–4 wk", l: "Lead Time" },
          ].map((b) => (
            <div key={b.l} className="flex flex-col items-center text-center px-4">
              <div className="w-7 h-7 flex items-center justify-center mb-2">
                <i className={`${b.icon} text-amber-400 text-lg`}></i>
              </div>
              <span className="text-white text-xl font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{b.v}</span>
              <span className="text-stone-400 text-xs mt-0.5" style={{ fontFamily: "'Raleway', sans-serif" }}>{b.l}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Intro */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <span className="text-amber-600 text-xs font-bold tracking-widest uppercase" style={{ fontFamily: "'Raleway', sans-serif" }}>The Right Window for Your Home</span>
            <h2 className="text-4xl font-bold text-zinc-900 mt-3 mb-5" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              uPVC or Aluminium — We Supply &amp; Install Both
            </h2>
            <div className="space-y-4 text-zinc-500 text-sm leading-relaxed" style={{ fontFamily: "'Raleway', sans-serif" }}>
              <p>
                Replacing your windows is one of the most impactful home improvements you can make — for comfort, appearance, energy efficiency, and property value. At Ensure Windows, we specialise in both <strong className="text-zinc-700">uPVC windows</strong> and <strong className="text-zinc-700">aluminium windows</strong> for homes across Caterham, Surrey, and South London.
              </p>
              <p>
                Our uPVC windows are manufactured on 70mm 6-chamber profiles, achieving A+ Window Energy Ratings as standard. Our aluminium windows use thermally broken profiles with slim sightlines, allowing more glass and more light — ideal for contemporary homes and extensions.
              </p>
              <p>
                Every window we supply is <strong className="text-zinc-700">made to measure</strong>, fitted by our own FENSA-registered installation team, and backed by a 10-year guarantee. We cover all of Surrey including Caterham, Redhill, Reigate, Oxted, and surrounding areas.
              </p>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              {["FENSA Registered", "A+ Energy Rated", "PAS 24 Security", "Made to Measure", "10 Year Guarantee"].map((badge) => (
                <span key={badge} className="inline-flex items-center gap-1.5 bg-stone-50 border border-stone-200 text-zinc-600 text-xs font-semibold px-3 py-1.5 rounded-full" style={{ fontFamily: "'Raleway', sans-serif" }}>
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-checkbox-circle-fill text-amber-500 text-xs"></i>
                  </div>
                  {badge}
                </span>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="w-full h-72 rounded-lg overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=white%20upvc%20casement%20window%20close%20up%20detail%20frame%20profile%20clean%20residential%20uk%20home%20bright%20light%20interior%20clean%20lines&width=400&height=290&seq=win-intro-upvc&orientation=portrait"
                alt="uPVC windows Caterham Surrey"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="w-full h-72 rounded-lg overflow-hidden mt-6">
              <img
                src="https://readdy.ai/api/search-image?query=anthracite%20grey%20aluminium%20slim%20window%20frame%20contemporary%20uk%20home%20exterior%20modern%20architecture%20precision%20engineering%20black%20frame%20garden&width=400&height=290&seq=win-intro-alum&orientation=portrait"
                alt="Aluminium windows Surrey"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="col-span-2 bg-amber-50 rounded-lg p-5 border border-amber-100">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg">
                  <i className="ri-phone-line text-amber-600"></i>
                </div>
                <p className="text-zinc-700 font-bold text-sm" style={{ fontFamily: "'Raleway', sans-serif" }}>Speak to our Surrey window team</p>
              </div>
              <p className="text-zinc-500 text-xs mb-3" style={{ fontFamily: "'Raleway', sans-serif" }}>Free no-obligation surveys across Caterham, Redhill and all of Surrey.</p>
              <a href="tel:07852636300" className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-500 text-white text-sm font-semibold px-5 py-2.5 rounded-md transition-colors whitespace-nowrap cursor-pointer" style={{ fontFamily: "'Raleway', sans-serif" }}>
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-phone-fill text-sm"></i></div>
                07852 636 300
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Window Styles */}
      <section id="window-styles" className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="text-amber-600 text-xs font-bold tracking-widest uppercase" style={{ fontFamily: "'Raleway', sans-serif" }}>Window Styles</span>
            <h2 className="text-4xl font-bold text-zinc-900 mt-3 mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Six Styles. Endless Possibilities.
            </h2>
            <p className="text-zinc-500 text-sm" style={{ fontFamily: "'Raleway', sans-serif" }}>
              From the most popular casement to elegant vertical sliders, find the window style that suits your home's character and your budget.
            </p>
          </div>

          {/* Filter tabs */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex bg-white border border-stone-200 rounded-full p-1 gap-1">
              {(["all", "popular", "premium", "specialist"] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => setActiveFilter(f)}
                  className={`px-5 py-2 rounded-full text-sm font-semibold transition-all whitespace-nowrap cursor-pointer capitalize ${
                    activeFilter === f ? "bg-amber-600 text-white" : "text-zinc-500 hover:text-zinc-700"
                  }`}
                  style={{ fontFamily: "'Raleway', sans-serif" }}
                >
                  {f === "all" ? "All Styles" : f}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((w) => (
              <WindowTypeCard key={w.id} window={w} onQuote={handleQuote} />
            ))}
          </div>
        </div>
      </section>

      {/* Energy Ratings */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-amber-600 text-xs font-bold tracking-widest uppercase" style={{ fontFamily: "'Raleway', sans-serif" }}>Thermal Performance</span>
            <h2 className="text-4xl font-bold text-zinc-900 mt-3 mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              A+ Energy Ratings as Standard
            </h2>
            <p className="text-zinc-500 text-sm" style={{ fontFamily: "'Raleway', sans-serif" }}>
              Every window we supply meets or exceeds the BFRC Window Energy Rating A+, well above current UK Building Regulations requirements.
            </p>
          </div>

          {/* WER visual */}
          <div className="grid lg:grid-cols-3 gap-8 items-start mb-14">
            <div className="lg:col-span-1">
              <div className="rounded-xl overflow-hidden border border-stone-100">
                <div className="bg-zinc-900 px-6 py-4">
                  <p className="text-white font-bold text-sm" style={{ fontFamily: "'Raleway', sans-serif" }}>BFRC Window Energy Ratings</p>
                  <p className="text-stone-400 text-xs mt-0.5" style={{ fontFamily: "'Raleway', sans-serif" }}>British Fenestration Rating Council</p>
                </div>
                <div className="bg-white p-4">
                  {[
                    { label: "A++", pct: 100, colour: "bg-green-600", active: false },
                    { label: "A+", pct: 86, colour: "bg-green-500", active: true },
                    { label: "A", pct: 72, colour: "bg-lime-500", active: false },
                    { label: "B", pct: 58, colour: "bg-yellow-400", active: false },
                    { label: "C", pct: 44, colour: "bg-orange-400", active: false, regs: true },
                    { label: "D", pct: 30, colour: "bg-red-400", active: false },
                    { label: "E–G", pct: 16, colour: "bg-red-600", active: false },
                  ].map((row) => (
                    <div key={row.label} className="flex items-center gap-3 mb-2">
                      <span className={`text-xs font-bold w-8 text-center ${row.active ? "text-green-700" : "text-zinc-400"}`} style={{ fontFamily: "'Raleway', sans-serif" }}>
                        {row.label}
                      </span>
                      <div className="flex-1 bg-stone-100 rounded-full h-6 overflow-hidden relative">
                        <div className={`h-full ${row.colour} rounded-full`} style={{ width: `${row.pct}%` }}></div>
                        {row.active && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className="text-[10px] font-bold text-white" style={{ fontFamily: "'Raleway', sans-serif" }}>Our Standard</span>
                          </div>
                        )}
                        {row.regs && (
                          <div className="absolute right-2 inset-y-0 flex items-center">
                            <span className="text-[9px] text-zinc-500" style={{ fontFamily: "'Raleway', sans-serif" }}>Building Regs min</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-5">
              {energyFeatures.map((feat) => (
                <div key={feat.title} className="bg-stone-50 rounded-xl p-6 border border-stone-100">
                  <div className="flex items-start gap-4 mb-3">
                    <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center bg-amber-100 rounded-xl">
                      <i className={`${feat.icon} text-amber-600 text-xl`}></i>
                    </div>
                    <div>
                      <p className="text-amber-600 text-2xl font-bold leading-none" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                        {feat.rating}
                        {feat.ratingUnit && <span className="text-base text-zinc-400 ml-1 font-normal">{feat.ratingUnit}</span>}
                      </p>
                      <h3 className="text-zinc-800 font-bold text-sm mt-0.5" style={{ fontFamily: "'Raleway', sans-serif" }}>{feat.title}</h3>
                    </div>
                  </div>
                  <p className="text-zinc-500 text-xs leading-relaxed" style={{ fontFamily: "'Raleway', sans-serif" }}>{feat.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* U-Value comparison bar */}
          <div className="bg-stone-50 rounded-xl border border-stone-100 p-7">
            <h3 className="text-lg font-bold text-zinc-800 mb-5" style={{ fontFamily: "'Cormorant Garamond', serif" }}>U-Value Comparison — How We Measure Up</h3>
            <div className="space-y-4">
              {[
                { label: "Single Glazed (old)", uValue: 5.8, pct: 100, colour: "bg-red-500" },
                { label: "Building Regs minimum", uValue: 1.6, pct: 40, colour: "bg-orange-400" },
                { label: "Our uPVC Standard", uValue: 1.2, pct: 28, colour: "bg-lime-500" },
                { label: "Our Aluminium Standard", uValue: 1.0, pct: 23, colour: "bg-green-500" },
                { label: "Triple Glazed Upgrade", uValue: 0.8, pct: 17, colour: "bg-green-700" },
              ].map((row) => (
                <div key={row.label} className="flex items-center gap-4">
                  <span className="text-zinc-600 text-xs w-44 flex-shrink-0" style={{ fontFamily: "'Raleway', sans-serif" }}>{row.label}</span>
                  <div className="flex-1 bg-stone-200 rounded-full h-5 overflow-hidden">
                    <div className={`h-full ${row.colour} rounded-full transition-all duration-700`} style={{ width: `${row.pct}%` }}></div>
                  </div>
                  <span className="text-zinc-800 text-xs font-bold w-20 text-right" style={{ fontFamily: "'Raleway', sans-serif" }}>{row.uValue} W/m²K</span>
                </div>
              ))}
            </div>
            <p className="text-zinc-400 text-xs mt-4" style={{ fontFamily: "'Raleway', sans-serif" }}>Lower U-value = better thermal performance. U-values shown are for complete window unit (frame + glass).</p>
          </div>
        </div>
      </section>

      {/* Technical Specs */}
      <section id="specs" className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-amber-600 text-xs font-bold tracking-widest uppercase" style={{ fontFamily: "'Raleway', sans-serif" }}>Technical Specifications</span>
            <h2 className="text-4xl font-bold text-zinc-900 mt-3 mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              uPVC vs Aluminium — Full Spec Breakdown
            </h2>
            <p className="text-zinc-500 text-sm" style={{ fontFamily: "'Raleway', sans-serif" }}>
              Compare both materials side by side or view each specification in full. Not sure which to choose? We can advise during your free survey.
            </p>
          </div>
          <SpecsComparison />
        </div>
      </section>

      {/* Security Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div className="w-full h-[400px] rounded-lg overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=secure%20window%20lock%20multi%20point%20locking%20mechanism%20upvc%20casement%20close%20up%20hardware%20detail%20security%20residential%20uk%20home%20modern&width=700&height=400&seq=win-security&orientation=landscape"
                alt="Window security hardware Surrey"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div>
              <span className="text-amber-600 text-xs font-bold tracking-widest uppercase" style={{ fontFamily: "'Raleway', sans-serif" }}>Security</span>
              <h2 className="text-4xl font-bold text-zinc-900 mt-3 mb-5" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Security Built In, Not Bolted On
              </h2>
              <div className="space-y-4 text-zinc-500 text-sm leading-relaxed mb-7" style={{ fontFamily: "'Raleway', sans-serif" }}>
                <p>Every window we install includes <strong className="text-zinc-700">PAS 24 enhanced security hardware</strong> as standard — the specification required by Secured by Design and most buildings insurers. This is not an optional upgrade; it is our baseline.</p>
                <p>All windows are glazed with <strong className="text-zinc-700">laminated or toughened safety glass</strong> as appropriate. For ground floor and accessible windows, we recommend our Secured by Design certified units with anti-jemmy protection and 3D adjustable hinges.</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: "ri-lock-2-line", title: "PAS 24 Hardware", desc: "Multi-point shootbolt locking as standard on all casement & tilt-turn windows" },
                  { icon: "ri-shield-star-line", title: "Secured by Design", desc: "Police-backed accreditation available on request — ideal for insurance requirements" },
                  { icon: "ri-eye-line", title: "Anti-Jemmy Beads", desc: "External glazing beads prevent glass removal from outside on all styles" },
                  { icon: "ri-building-line", title: "Building Regs Compliant", desc: "FENSA certified installation ensures legal compliance for all window replacements" },
                ].map((item) => (
                  <div key={item.title} className="flex items-start gap-3">
                    <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center bg-amber-50 rounded-lg">
                      <i className={`${item.icon} text-amber-600`}></i>
                    </div>
                    <div>
                      <p className="text-zinc-800 text-sm font-semibold mb-0.5" style={{ fontFamily: "'Raleway', sans-serif" }}>{item.title}</p>
                      <p className="text-zinc-500 text-xs leading-relaxed" style={{ fontFamily: "'Raleway', sans-serif" }}>{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-amber-600 text-xs font-bold tracking-widest uppercase" style={{ fontFamily: "'Raleway', sans-serif" }}>Our Work</span>
            <h2 className="text-4xl font-bold text-zinc-900 mt-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Window Installations Across Surrey
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              { src: "https://readdy.ai/api/search-image?query=upvc%20casement%20windows%20white%20full%20house%20replacement%20before%20after%20surrey%20residential%20home%20exterior%20modern%20clean%20look%20street&width=600&height=400&seq=wing1&orientation=landscape", alt: "uPVC casement windows, Caterham Surrey" },
              { src: "https://readdy.ai/api/search-image?query=anthracite%20grey%20aluminium%20windows%20modern%20extension%20contemporary%20uk%20home%20dark%20frame%20glass%20garden%20exterior%20clean%20beautiful&width=600&height=400&seq=wing2&orientation=landscape", alt: "Aluminium windows, Redhill Surrey" },
              { src: "https://readdy.ai/api/search-image?query=vertical%20sliding%20sash%20windows%20white%20upvc%20victorian%20house%20replacement%20period%20property%20surrey%20heritage%20look%20modern%20performance&width=600&height=400&seq=wing3&orientation=landscape", alt: "Sash windows, Oxted Surrey" },
              { src: "https://readdy.ai/api/search-image?query=bay%20window%20upvc%20white%20three%20panel%20residential%20home%20exterior%20surrey%20uk%20traditional%20projection%20bright%20sitting%20room%20street%20view%20daytime&width=600&height=400&seq=wing4&orientation=landscape", alt: "Bay windows, Reigate Surrey" },
              { src: "https://readdy.ai/api/search-image?query=tilt%20and%20turn%20window%20interior%20upvc%20modern%20uk%20home%20open%20ventilation%20natural%20light%20clean%20white%20bright%20contemporary%20living%20space&width=600&height=400&seq=wing5&orientation=landscape", alt: "Tilt & turn windows, Purley" },
              { src: "https://readdy.ai/api/search-image?query=flush%20sash%20window%20upvc%20cream%20white%20modern%20uk%20home%20exterior%20conservation%20area%20clean%20lines%20architectural%20minimalist%20detail%20close%20up&width=600&height=400&seq=wing6&orientation=landscape", alt: "Flush sash windows, Warlingham Surrey" },
            ].map((img) => (
              <div key={img.src} className="w-full h-52 md:h-64 rounded-lg overflow-hidden group cursor-pointer">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/gallery" className="inline-flex items-center gap-2 text-amber-600 font-semibold hover:gap-3 transition-all cursor-pointer" style={{ fontFamily: "'Raleway', sans-serif" }}>
              View Full Project Gallery
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line"></i></div>
            </Link>
          </div>
        </div>
      </section>

      {/* Coverage Area */}
      <section className="py-16 bg-amber-600">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-white" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Serving Caterham, Surrey &amp; South London
            </h2>
            <p className="text-amber-100 text-sm mt-2" style={{ fontFamily: "'Raleway', sans-serif" }}>
              Window supply and installation across all of Surrey and South London
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {surreyCoverage.map((area) => (
              <span key={area} className="bg-white/20 hover:bg-white/30 text-white text-sm font-semibold px-5 py-2 rounded-full transition-colors" style={{ fontFamily: "'Raleway', sans-serif" }}>
                {area}
              </span>
            ))}
          </div>
          <p className="text-center text-amber-100/80 text-xs mt-6" style={{ fontFamily: "'Raleway', sans-serif" }}>
            Don&apos;t see your area? Call us on <a href="tel:07852636300" className="text-white font-semibold underline cursor-pointer">07852 636 300</a> — we cover a wide radius from our Caterham base.
          </p>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-amber-600 text-xs font-bold tracking-widest uppercase" style={{ fontFamily: "'Raleway', sans-serif" }}>How It Works</span>
            <h2 className="text-4xl font-bold text-zinc-900 mt-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Our Simple 4-Step Process</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.map((s, i) => (
              <div key={s.step} className="relative text-center">
                {i < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-8 left-[calc(50%+2rem)] right-0 border-t-2 border-dashed border-amber-200 z-0"></div>
                )}
                <div className="relative z-10 w-16 h-16 flex items-center justify-center bg-amber-600 rounded-full mx-auto mb-5 text-white text-2xl font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  {s.step}
                </div>
                <h3 className="text-lg font-bold text-zinc-800 mb-2" style={{ fontFamily: "'Raleway', sans-serif" }}>{s.title}</h3>
                <p className="text-zinc-500 text-sm leading-relaxed" style={{ fontFamily: "'Raleway', sans-serif" }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-amber-600 text-xs font-bold tracking-widest uppercase" style={{ fontFamily: "'Raleway', sans-serif" }}>Customer Reviews</span>
            <h2 className="text-4xl font-bold text-zinc-900 mt-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>What Our Window Customers Say</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {windowTestimonials.map((t) => (
              <div key={t.name} className="bg-white rounded-lg p-7 border border-stone-100">
                <div className="flex gap-1 mb-3">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-amber-500 text-sm"></i>
                    </div>
                  ))}
                </div>
                <span className="inline-block bg-amber-50 text-amber-700 text-[10px] font-bold px-2 py-0.5 rounded-full mb-3" style={{ fontFamily: "'Raleway', sans-serif" }}>
                  {t.product}
                </span>
                <p className="text-zinc-600 text-sm leading-relaxed mb-6 italic" style={{ fontFamily: "'Raleway', sans-serif" }}>
                  &ldquo;{t.text}&rdquo;
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-amber-600 rounded-full text-white text-xs font-bold">
                    {t.avatar}
                  </div>
                  <div>
                    <p className="text-zinc-800 text-sm font-semibold" style={{ fontFamily: "'Raleway', sans-serif" }}>{t.name}</p>
                    <p className="text-zinc-400 text-xs" style={{ fontFamily: "'Raleway', sans-serif" }}>{t.location}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-amber-600 text-xs font-bold tracking-widest uppercase" style={{ fontFamily: "'Raleway', sans-serif" }}>FAQ</span>
            <h2 className="text-4xl font-bold text-zinc-900 mt-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Frequently Asked Questions</h2>
          </div>
          <div className="space-y-3">
            {windowFaqs.map((faq, i) => (
              <div key={i} className="border border-stone-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left cursor-pointer bg-white hover:bg-stone-50 transition-colors"
                >
                  <span className="font-semibold text-zinc-800 text-sm pr-4" style={{ fontFamily: "'Raleway', sans-serif" }}>{faq.q}</span>
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className={`${openFaq === i ? "ri-subtract-line" : "ri-add-line"} text-amber-600`}></i>
                  </div>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5 bg-stone-50">
                    <p className="text-zinc-500 text-sm leading-relaxed" style={{ fontFamily: "'Raleway', sans-serif" }}>{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Form */}
      <section id="quote-form" ref={formRef} className="py-20" style={{ background: "linear-gradient(135deg, #1a1a1a 0%, #2d1f0e 100%)" }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-14 items-start">
            <div>
              <span className="text-amber-400 text-xs font-bold tracking-widest uppercase" style={{ fontFamily: "'Raleway', sans-serif" }}>Get Started</span>
              <h2 className="text-4xl font-bold text-white mt-4 mb-5" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Get Your Free Window Quote in Surrey
              </h2>
              <p className="text-stone-300 text-sm leading-relaxed mb-8" style={{ fontFamily: "'Raleway', sans-serif" }}>
                Contact our Surrey window team today for a free, no-obligation survey and written quotation. We cover Caterham, Redhill, Reigate, Oxted and all surrounding areas.
              </p>
              <div className="flex flex-col gap-5 mb-8">
                {[
                  { icon: "ri-phone-line", text: "07852 636 300", href: "tel:07852636300" },
                  { icon: "ri-mail-line", text: "sales@ensurewindows.co.uk", href: "mailto:sales@ensurewindows.co.uk" },
                  { icon: "ri-map-pin-2-line", text: "Seven House, Town End, Caterham CR3 5UG", href: "#" },
                ].map((item) => (
                  <a key={item.text} href={item.href} className="flex items-center gap-3 cursor-pointer group">
                    <div className="w-9 h-9 flex items-center justify-center bg-amber-600/20 rounded-md">
                      <i className={`${item.icon} text-amber-400`}></i>
                    </div>
                    <span className="text-stone-300 group-hover:text-amber-400 transition-colors text-sm" style={{ fontFamily: "'Raleway', sans-serif" }}>{item.text}</span>
                  </a>
                ))}
              </div>
              <div className="border-t border-white/10 pt-7">
                <p className="text-stone-400 text-xs mb-4" style={{ fontFamily: "'Raleway', sans-serif" }}>Why choose Ensure Windows?</p>
                <div className="grid grid-cols-2 gap-3">
                  {["FENSA Registered Installers", "A+ Energy Rated Windows", "Free Written Quotations", "10-Year Guarantees", "No Hidden Charges", "Local Surrey Specialists"].map((item) => (
                    <div key={item} className="flex items-center gap-2">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-checkbox-circle-fill text-amber-400 text-sm"></i>
                      </div>
                      <span className="text-stone-300 text-xs" style={{ fontFamily: "'Raleway', sans-serif" }}>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-8">
              {submitted ? (
                <div className="text-center py-10">
                  <div className="w-16 h-16 flex items-center justify-center bg-amber-600/20 rounded-full mx-auto mb-4">
                    <i className="ri-check-line text-amber-400 text-3xl"></i>
                  </div>
                  <h3 className="text-white text-xl font-bold mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Thank You!</h3>
                  <p className="text-stone-300 text-sm" style={{ fontFamily: "'Raleway', sans-serif" }}>
                    We&apos;ll be in touch within 24 hours to arrange your free survey across Surrey.
                  </p>
                </div>
              ) : (
                <>
                  <h3 className="text-white text-xl font-bold mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Request a Free Survey &amp; Quote</h3>
                  <form onSubmit={handleSubmit} data-readdy-form className="space-y-4">
                    <input
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                      placeholder="Your Full Name"
                      className="w-full bg-white/10 border border-white/20 text-white placeholder-stone-400 text-sm px-4 py-3 rounded-md focus:outline-none focus:border-amber-400"
                      style={{ fontFamily: "'Raleway', sans-serif" }}
                    />
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      required
                      placeholder="Email Address"
                      className="w-full bg-white/10 border border-white/20 text-white placeholder-stone-400 text-sm px-4 py-3 rounded-md focus:outline-none focus:border-amber-400"
                      style={{ fontFamily: "'Raleway', sans-serif" }}
                    />
                    <input
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="Phone Number"
                      className="w-full bg-white/10 border border-white/20 text-white placeholder-stone-400 text-sm px-4 py-3 rounded-md focus:outline-none focus:border-amber-400"
                      style={{ fontFamily: "'Raleway', sans-serif" }}
                    />
                    <select
                      name="windowType"
                      value={form.windowType}
                      onChange={handleChange}
                      className="w-full bg-white/10 border border-white/20 text-stone-300 text-sm px-4 py-3 rounded-md focus:outline-none focus:border-amber-400 cursor-pointer"
                      style={{ fontFamily: "'Raleway', sans-serif" }}
                    >
                      <option value="">Window style (if known)</option>
                      <option>Casement Windows</option>
                      <option>Tilt &amp; Turn Windows</option>
                      <option>Vertical Sliding Sash</option>
                      <option>Bay &amp; Bow Windows</option>
                      <option>Flush Sash Windows</option>
                      <option>Fixed / Picture Windows</option>
                      <option>Not sure yet — need advice</option>
                    </select>
                    <select
                      name="material"
                      className="w-full bg-white/10 border border-white/20 text-stone-300 text-sm px-4 py-3 rounded-md focus:outline-none focus:border-amber-400 cursor-pointer"
                      style={{ fontFamily: "'Raleway', sans-serif" }}
                      defaultValue=""
                    >
                      <option value="">Material preference</option>
                      <option>uPVC</option>
                      <option>Aluminium</option>
                      <option>Not sure — happy to be advised</option>
                    </select>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      rows={3}
                      maxLength={500}
                      placeholder="Tell us about your project, number of windows, or any questions…"
                      className="w-full bg-white/10 border border-white/20 text-white placeholder-stone-400 text-sm px-4 py-3 rounded-md focus:outline-none focus:border-amber-400 resize-none"
                      style={{ fontFamily: "'Raleway', sans-serif" }}
                    />
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-amber-600 hover:bg-amber-500 text-white font-semibold py-3.5 rounded-md transition-colors whitespace-nowrap cursor-pointer"
                      style={{ fontFamily: "'Raleway', sans-serif" }}
                    >
                      {submitting ? "Sending…" : "Request Free Survey & Quote"}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
